import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import ApiReponseViewer from "../../../../components/apiResponseViewer/apiReponseViewer";
import BadgeWithIcon from "../../../../components/badgeWithIcon/badgeWithIcon";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import DocViewerModal from "../../../../components/modals/docViewerModal/docViewerModal";
import ImageviewerModal from "../../../../components/modals/imageViewerModal/imageviewerModal";
import {
  EventBadgeBgColorForUI,
  EventBadgeTextColorForUI,
  EventType,
  EventTypeTextForUI,
} from "../../../../enums/eventType";
import { ModalId } from "../../../../enums/modalId";
import { getAuditTrialDetails } from "../../../../redux/slice/auditTrialSlice";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { CrossIcon } from "../../../../svg/cross-icon";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";
import { checkForClickJS } from "../../../viewAllEventsLayout/helpers/viewAllAuditTrailTableGenerator";
import {
  AuditTrialDetailsTableColumnHeaders,
  generateAuditTrialExhaustiveTableData,
} from "../../utils/auditTrialTableDataGenerator";
import AssistSessionViewer from "../assistSessionViewer/assistSessionViewer";

interface AuditTrialSubEventsProps {
  onClose: () => void;
}

const AuditTrialSubEvents = (props: AuditTrialSubEventsProps) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const { data, isLoading } = useSelector(
    (state: RootState) => state.auditTrial,
  );

  const [apiRequest, setApiRequest] = useState<Record<string, any>[]>([]);
  const [selectedApiRequest, setSelectedApiRequest] = useState<number>(0);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (data) {
      if (data.tag === EventType.API_CALL) {
        setApiRequest(data?.entries ?? []);
        setSelectedApiRequest(0);
      } else if (data.tag !== EventType.EXTENSION_SESSION) {
        const tableData = generateAuditTrialExhaustiveTableData(data);
        setTableData(tableData);
      }
    } else {
      setTableData([]);
    }
  }, [data]);

  const refetchAuditTrialDetails = () => {
    dispatch(
      getAuditTrialDetails(
        data?.order_id ?? "",
        data?.name ?? "",
        data?.tag ?? "",
      ),
    );
  };

  const getEventTypeTagAndColor = (eventType: string, apiName: string) => {
    if (
      apiName.toLowerCase().includes("flatiron") &&
      eventType.toLowerCase().includes("api")
    ) {
      return {
        text: EventTypeTextForUI[EventType.FHIR_API_CALL],
        bgColor: EventBadgeBgColorForUI[EventType.FHIR_API_CALL],
        color: EventBadgeTextColorForUI[EventType.FHIR_API_CALL],
      };
    } else if (
      !apiName.toLowerCase().includes("fhir") &&
      eventType.toLowerCase().includes("api")
    ) {
      return {
        text: EventTypeTextForUI[EventType.AI_AGENT],
        bgColor: EventBadgeBgColorForUI[EventType.AI_AGENT],
        color: EventBadgeTextColorForUI[EventType.AI_AGENT],
      };
    }
    return {
      text: EventTypeTextForUI[eventType],
      bgColor: EventBadgeBgColorForUI[eventType],
      color: EventBadgeTextColorForUI[eventType],
    };
  };

  useEffect(() => {
    if (openedModalId === ModalId.IMAGE_VIEWER_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_MODAL) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
  }, [openedModalId]);

  return (
    <div className="audit-trial-sub-events--container flex h-full flex-col bg-white">
      {isLoading ? (
        <div className="loader-message--container flex h-full items-center justify-center">
          <LoaderMessage message="Loading audit trial sub events..." />
        </div>
      ) : (
        <>
          <div className="header_container flex flex-shrink-0 items-start gap-3 border-b border-primaryGray-14 px-5 py-3">
            <div className="event-name-with-timestamp flex flex-col gap-2 text-lg font-bold">
              <div className="event-with-tag flex gap-2">
                <div>
                  {capitalizeWordsSeperatedByUnderScore(
                    checkForClickJS(data?.name ?? ""),
                  )}
                </div>
                <div className="event-tag">
                  <BadgeWithIcon
                    text={
                      getEventTypeTagAndColor(
                        data?.tag ?? "",
                        data?.entries?.[selectedApiRequest]?.metadata
                          ?.api_name ?? "",
                      ).text ?? ""
                    }
                    id="event-tag"
                    textColor={
                      getEventTypeTagAndColor(
                        data?.tag ?? "",
                        data?.entries?.[selectedApiRequest]?.metadata
                          ?.api_name ?? "",
                      ).color ?? "black"
                    }
                    bgColor={
                      getEventTypeTagAndColor(
                        data?.tag ?? "",
                        data?.entries?.[selectedApiRequest]?.metadata
                          ?.api_name ?? "",
                      ).bgColor ?? "yellow"
                    }
                  />
                </div>
              </div>
            </div>
            <div className="mb-auto ml-auto mt-auto cursor-pointer">
              <CrossIcon onClick={props.onClose} />
            </div>
          </div>
          <div className="body--container min-h-0 flex-1 px-4 pb-4 pt-0">
            {data && data.tag === EventType.API_CALL ? (
              <ApiReponseViewer
                apiName={
                  apiRequest[selectedApiRequest]?.metadata?.api_name ?? ""
                }
                totalEntries={data?.total_entries ?? 0}
                selectedApiRequest={selectedApiRequest}
                setSelectedApiRequest={setSelectedApiRequest}
                requestData={
                  apiRequest[selectedApiRequest]?.metadata?.request_data ?? {}
                }
                responseData={
                  apiRequest[selectedApiRequest]?.metadata?.response_data ?? {}
                }
                statusCode={
                  apiRequest[selectedApiRequest]?.metadata?.status_code ?? 0
                }
                shouldHaveTopMargin={true}
              />
            ) : data?.tag === EventType.EXTENSION_SESSION ? (
              <AssistSessionViewer
                entries={data?.entries ?? []}
                onRefresh={refetchAuditTrialDetails}
              />
            ) : (
              <div className="h-full overflow-auto">
                <CustomTable
                  tableHeaders={AuditTrialDetailsTableColumnHeaders}
                  tableData={tableData}
                  tableName="audit_trial_sub_events"
                  pagesPerView={5}
                  itemsPerPage={tableData.length}
                  count={tableData.length}
                  hideSearchBar={true}
                  hidePagination={true}
                  showRefreshButton={true}
                  onRefreshButton={refetchAuditTrialDetails}
                />
              </div>
            )}
          </div>
        </>
      )}
      <>
        {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
          <ImageviewerModal
            fileUrls={[]}
            filePaths={[metaData?.filePaths ?? []]}
            onClose={() => {
              dispatch(closeModal());
            }}
            shouldHideZoom={true}
          />
        )}
        {openedModalId === ModalId.DOC_VIEWER_MODAL && (
          <DocViewerModal
            onClose={() => dispatch(closeModal())}
            type="clinical_attachment"
          />
        )}
      </>
    </div>
  );
};

export default AuditTrialSubEvents;
